<template>
<div class="profile-onboarding-shell">
  <div v-if="is_loading" class="profile-loading-state glassy-table border rounded-lg p-5 text-center shadow-sm">
    <div class="spinner-border text-primary mx-auto" role="status"></div>
    <p class="text-muted mt-3 mb-0 text-xxs">Loading profile...</p>
  </div>

  <div v-else-if="!has_profile_form" class="profile-empty-state glassy-table border rounded-lg shadow-sm">
    <div class="card-body p-4 text-center py-5">
      <i class="bi bi-file-earmark-x fs-1 text-muted opacity-50"></i>
      <h6 class="mt-3 mb-1 fw-bold text-dark">No Profile Form Assigned</h6>
      <p class="text-muted text-xxs mb-0">A profile form has not been assigned to your account yet.</p>
    </div>
  </div>

  <div v-else class="profile-forms-container">

    <div class="profile-acc-list">
      <div
        v-for="(section, index) in profile_flow_sections"
        :key="section.key"
        class="profile-acc-item glassy-table"
      >
        <button
          type="button"
          class="profile-acc-head"
          @click="toggleSection(index)"
        >
          <span class="profile-acc-num d-none">{{ index + 1 }}</span>
          <span class="profile-acc-title-wrap">
            <span class="profile-acc-title">{{ section.title }}</span>
            <span class="profile-acc-subtitle">{{ getSectionAnsweredLabel(section) }}</span>
          </span>
          <i
            class="bi"
            :class="current_section_index === index ? 'bi-chevron-up' : 'bi-chevron-down'"
          ></i>
        </button>

        <div v-if="current_section_index === index" class="profile-acc-body">
          <div class="profile-acc-body-toolbar">
            <p class="text-muted text-xxs mb-0">Preview information below.</p>
            <button
              class="btn btn-sm rounded-small text-xs shadow text-dark btn-primary-dark d-flex align-items-center fw-bold"
              @click="is_editing ? update_profile() : (is_editing = true)"
              :disabled="is_saving"
            >
              <span v-if="is_saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ is_editing ? 'Update' : 'Edit' }}
            </button>
          </div>

          <template v-if="section.kind === 'account'">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="fw-medium text-x text-start d-block mt-2">First Name</label>
                <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :disabled="!is_editing" v-model="account_details.first_name" placeholder="First name">
              </div>
              <div class="col-md-6">
                <label class="fw-medium text-x text-start d-block mt-2">Last Name</label>
                <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :disabled="!is_editing" v-model="account_details.last_name" placeholder="Last name">
              </div>
              <div class="col-md-6">
                <label class="fw-medium text-x text-start d-block mt-2">Email Address</label>
                <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" type="email" :disabled="!is_editing" v-model="account_details.email" placeholder="email@example.com">
              </div>
              <div class="col-md-6">
                <label class="fw-medium text-x text-start d-block mt-2">Phone Number</label>
                <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :disabled="!is_editing" v-model="account_details.phone_number" placeholder="Phone number">
              </div>
              <div class="col-md-6">
                <label class="fw-medium text-x text-start d-block mt-2">Date Joined</label>
                <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :value="account_details.date_joined || 'Not provided'" disabled>
              </div>
            </div>
            <div v-if="is_editing" class="profile-subsection-card mt-4">
              <div class="d-flex align-items-center gap-2 mb-3">
                <i class="bi bi-shield-lock text-warning"></i>
                <p class="mb-0 fw-bold text-xs">Change Password</p>
                <span class="badge bg-light text-muted text-xxs ms-2">Optional</span>
              </div>
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="fw-medium text-x text-start d-block mt-2">New Password</label>
                  <div class="input-group">
                    <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :type="password_input_type" v-model="new_password" placeholder="Enter new password">
                    <button class="btn btn-outline-secondary text-xxs" type="button" @click="toggle_password_visibility">
                      <i :class="password_input_type === 'password' ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
                    </button>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="fw-medium text-x text-start d-block mt-2">Confirm Password</label>
                  <div class="input-group">
                    <input class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :type="confirm_password_input_type" v-model="confirm_password" placeholder="Confirm password">
                    <button class="btn btn-outline-secondary text-xxs" type="button" @click="toggle_confirm_password_visibility">
                      <i :class="confirm_password_input_type === 'password' ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="section.kind === 'section'">
            <div class="row g-3">
              <div
                v-for="question in section.questions"
                :key="question.id"
                :class="(question.question_type === 'single_select' || question.question_type === 'multi_select') ? 'col-12' : 'col-md-6'"
              >
                <label class="fw-medium text-x text-start d-block mt-2">{{ question.label }}</label>

                <textarea
                  v-if="question.question_type === 'long_text'"
                  class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                  rows="3" :disabled="!is_editing" v-model="question.editable_value" placeholder="Enter your response..."
                  @input="update_profile_dependent_questions_visibility(question, question.editable_value)"
                ></textarea>

                <div v-else-if="question.question_type === 'single_select'" class="mt-2 profile-choice-group">
                  <div v-if="question.options && question.options.length" class="d-flex flex-wrap gap-3">
                    <div v-for="(opt, optIdx) in question.options" :key="'ss-' + question.id + '-' + optIdx" class="form-check mb-1">
                      <input class="form-check-input" type="radio" :name="'q-' + question.id" :id="'q-' + question.id + '-opt-' + optIdx" :value="opt.value" v-model="question.editable_value" :disabled="!is_editing" @change="update_profile_dependent_questions_visibility(question, question.editable_value)">
                      <label class="form-check-label text-xs ms-1" :for="'q-' + question.id + '-opt-' + optIdx">{{ opt.text }}</label>
                    </div>
                  </div>
                  <p v-else class="text-muted text-xs mb-0 fst-italic">No options available</p>
                </div>

                <div v-else-if="question.question_type === 'multi_select'" class="mt-2 profile-choice-group">
                  <div v-if="question.options && question.options.length" class="d-flex flex-wrap gap-3">
                    <div v-for="(opt, optIdx) in question.options" :key="'ms-' + question.id + '-' + optIdx" class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" :id="'q-' + question.id + '-opt-' + optIdx" :value="opt.value" v-model="question.editable_value" :disabled="!is_editing" @change="update_profile_dependent_questions_visibility(question, question.editable_value)">
                      <label class="form-check-label text-xs ms-1" :for="'q-' + question.id + '-opt-' + optIdx">{{ opt.text }}</label>
                    </div>
                  </div>
                  <p v-else class="text-muted text-xs mb-0 fst-italic">No options available</p>
                </div>

                <select
                  v-else-if="question.question_type === 'dropdown'"
                  class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                  :disabled="!is_editing" v-model="question.editable_value" @change="update_profile_dependent_questions_visibility(question, question.editable_value)"
                >
                  <option value="">Select an option</option>
                  <option v-for="(opt, optIdx) in question.options" :key="'dd-' + question.id + '-' + optIdx" :value="opt.value">{{ opt.text }}</option>
                </select>

                <input v-else-if="question.question_type === 'date'" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" type="date" :disabled="!is_editing" v-model="question.editable_value" @change="update_profile_dependent_questions_visibility(question, question.editable_value)">

                <input v-else-if="question.question_type === 'number'" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" type="number" :disabled="!is_editing" v-model="question.editable_value" placeholder="Enter a number..." @input="update_profile_dependent_questions_visibility(question, question.editable_value)">

                <div v-else-if="is_phone_question_type(question.question_type)">
                  <input
                    type="tel"
                    class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                    :class="{ 'border-danger': profile_validation_errors[question.id] }"
                    :disabled="!is_editing"
                    :value="question.editable_value"
                    placeholder="+27 82 123 4567"
                    maxlength="22"
                    @input="on_profile_phone_input(question, $event)"
                    @blur="on_profile_phone_blur(question, Boolean(question.is_required || question.is_mandatory), $event)"
                    @change="update_profile_dependent_questions_visibility(question, question.editable_value)"
                  >
                  <small v-if="profile_validation_errors[question.id]" class="text-danger text-xxs mt-1 d-block">
                    {{ profile_validation_errors[question.id] }}
                  </small>
                </div>

                <div v-else-if="is_file_question_type(question.question_type) || is_file_url(question.local_file_url || question.editable_value)" class="profile-file-row d-flex align-items-center gap-2 flex-wrap">
                  <input v-if="is_editing" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg w-auto" type="file" :accept="is_pdf_file_question(question) ? '.pdf,application/pdf' : ''" @change="handle_file_upload($event, question)">
                  <button v-if="is_file_url(question.local_file_url || question.editable_value)" type="button" class="btn p-0 border-0 bg-transparent shadow-none text-primary file-preview-icon-btn" title="Preview document" @mousedown.prevent @click="open_file_preview(question.local_file_url || question.editable_value, get_file_display_name(question.local_file_url || question.editable_value))">
                    <i class="bi bi-eye fs-6"></i>
                  </button>
                  <button v-if="is_editing && (question.local_file_url || question.editable_value)" type="button" class="btn btn-link p-0 text-danger" title="Remove and replace" @click="clear_file(question)">
                    <i class="bi bi-x-circle fs-6"></i>
                  </button>
                  <span v-if="question.editable_value" class="text-muted text-xxs">{{ get_file_display_name(question.editable_value) }}</span>
                  <span v-else class="text-muted text-xxs">No document uploaded</span>
                </div>

                <input v-else class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" :type="question.question_type === 'email' ? 'email' : 'text'" :disabled="!is_editing" v-model="question.editable_value" placeholder="Enter your response..." @input="update_profile_dependent_questions_visibility(question, question.editable_value)">

                <div v-if="question.dependent_questions && question.dependent_questions.length" class="mt-2 ms-4 ps-3 border-start border-3 border-primary">
                  <template v-for="row in flatten_profile_dependents(question.dependent_questions)" :key="row.key">
                    <div v-show="row.node.is_visible" class="mb-3" :class="row.depth >= 3 ? 'ms-4' : row.depth === 2 ? 'ms-3' : 'ms-0'">
                      <div class="d-flex align-items-start mb-1 option-row">
                        <label class="text-dark fw-7 text-xs text-capitalize option-label">
                          {{ row.node.display_label || row.node.label }}
                          <span v-if="row.node.is_required || row.node.is_mandatory" class="text-danger">*</span>
                        </label>
                      </div>

                      <input
                        v-if="row.node.question_type === 'email'"
                        type="email"
                        class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                        v-model="row.node.editable_value"
                        :disabled="!is_editing"
                        placeholder="Enter your response..."
                        @input="update_profile_dependent_questions_visibility(row.node, row.node.editable_value)"
                      >

                      <textarea
                        v-else-if="row.node.question_type === 'long_text'"
                        class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                        rows="3"
                        :disabled="!is_editing"
                        v-model="row.node.editable_value"
                        placeholder="Enter your response..."
                        @input="update_profile_dependent_questions_visibility(row.node, row.node.editable_value)"
                      ></textarea>

                      <select
                        v-else-if="row.node.question_type === 'single_select'"
                        class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                        v-model="row.node.editable_value"
                        :disabled="!is_editing"
                        @change="update_profile_dependent_questions_visibility(row.node, row.node.editable_value)"
                      >
                        <option value="">Select an option</option>
                        <option v-for="(opt, optIdx) in row.node.options || []" :key="row.node.id + '-opt-' + optIdx" :value="opt.value">{{ opt.text }}</option>
                      </select>

                      <input
                        v-else
                        class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                        :type="row.node.question_type === 'date' ? 'date' : row.node.question_type === 'number' ? 'number' : 'text'"
                        :disabled="!is_editing"
                        v-model="row.node.editable_value"
                        placeholder="Enter your response..."
                        @input="update_profile_dependent_questions_visibility(row.node, row.node.editable_value)"
                      >
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="['language', 'education', 'reference'].includes(section.kind)">
            <div v-for="(block, bidx) in section.blocks" :key="section.kind + '-pf-' + bidx" class="profile-we-block-card mb-4 p-4 border rounded-3">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <p class="fw-bold text-xs mb-0 text-dark"><i class="bi me-1" :class="get_structured_icon(section.kind)"></i>{{ block.label }}</p>
                <button v-if="is_editing && section.blocks.length > 1" class="btn btn-sm border-0 text-danger" @click="remove_structured_block(section, bidx)"><i class="bi bi-trash"></i></button>
              </div>

              <div class="row g-3">
                <div v-for="(field, fidx) in block.fields" :key="section.kind + '-f-' + bidx + '-' + fidx" class="col-md-4">
                  <label class="form-label fw-medium text-x text-start d-block">{{ field.label }}</label>

                  <select v-if="field.question_type === 'single_select'" class="form-select form-select-sm text-xs bg-light border-1 py-1 rounded-lg" v-model="field.editable_value" :disabled="!is_editing">
                    <option value="">Select...</option>
                    <option v-for="(opt, optIdx) in field.options" :key="field.id + '-opt-' + optIdx" :value="opt.value">{{ opt.text }}</option>
                  </select>

                  <div v-else-if="is_phone_question_type(field.question_type)">
                    <input
                      type="tel"
                      class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                      :class="{ 'border-danger': profile_validation_errors[field.id] }"
                      :value="field.editable_value"
                      :placeholder="field.label || '+27 82 123 4567'"
                      :disabled="!is_editing"
                      maxlength="22"
                      @input="on_profile_phone_input(field, $event)"
                      @blur="on_profile_phone_blur(field, Boolean(field.is_required || field.is_mandatory), $event)"
                    >
                    <small v-if="profile_validation_errors[field.id]" class="text-danger text-xxs mt-1 d-block">
                      {{ profile_validation_errors[field.id] }}
                    </small>
                  </div>

                  <input v-else type="text" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" v-model="field.editable_value" :placeholder="field.label" :disabled="!is_editing">

                  <input v-if="field.allows_other && String(field.editable_value || '').toLowerCase() === 'other'" type="text" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg mt-2" v-model="field.other_value" placeholder="Please specify" :disabled="!is_editing">
                </div>
              </div>
            </div>

            <div v-if="is_editing && section.blocks.length < (section.all_blocks || []).length" class="text-center mt-2 mb-3">
              <button class="btn btn-sm text-xxs shadow btn-primary-dark" @click="add_structured_block(section)">{{ get_structured_add_label(section.kind) }}</button>
            </div>
          </template>

          <template v-else-if="section.kind === 'work'">
            <div v-for="(block, bidx) in work_experience_blocks" :key="'we-pf-' + bidx" class="profile-we-block-card mb-4 p-4 border rounded-3">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <p class="fw-bold text-xs mb-0 text-dark"><i class="bi bi-building me-1"></i>{{ block.label }}</p>
                <button v-if="is_editing && work_experience_blocks.length > 1" class="btn btn-sm border-0 text-danger" @click="remove_we_block(bidx)"><i class="bi bi-trash"></i></button>
              </div>
              <div class="row g-3">
                <template v-for="(field, fidx) in block.fields" :key="'we-f-' + bidx + '-' + fidx">
                  <div
                    v-show="should_show_we_field(field, block)"
                    :class="['From Month', 'From Year', 'To Month', 'To Year'].includes(field.label) ? 'col-6 col-md-3' : (field.label === 'Reason for Leaving' ? 'col-12' : 'col-md-6')"
                  >
                    <label class="form-label fw-medium text-x text-start d-block">{{ field.label }}</label>
                    <select v-if="field.field_type === 'we_month'" class="form-select form-select-sm text-xs bg-light border-1 py-1 rounded-lg" v-model="field.editable_value" :disabled="!is_editing">
                      <option value="" disabled>Month</option>
                      <option v-for="(monthLabel, monthIndex) in ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']" :key="'mo-' + (monthIndex + 1)" :value="String(monthLabel)">{{ monthLabel }}</option>
                    </select>
                    <select v-else-if="field.field_type === 'we_year'" class="form-select form-select-sm text-xs bg-light border-1 py-1 rounded-lg" v-model="field.editable_value" :disabled="!is_editing">
                      <option value="" disabled>Year</option>
                      <option v-for="y in 57" :key="'yr-' + (2027 - y)" :value="String(2027 - y)">{{ 2027 - y }}</option>
                    </select>
                    <textarea v-else-if="field.field_type === 'textarea'" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" v-model="field.editable_value" rows="2" maxlength="200" placeholder="Reason for leaving" :disabled="!is_editing"></textarea>
                    <input v-else type="text" class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg" v-model="field.editable_value" :placeholder="field.label" :disabled="!is_editing">
                  </div>
                </template>
              </div>

              <div class="form-check mt-2 pt-2">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="'profile_cwh_' + bidx"
                  :checked="is_currently_working_block(block)"
                  :disabled="!is_editing"
                  @change="toggle_currently_work_here_profile(bidx)"
                >
                <label class="form-check-label text-xs text-dark" :for="'profile_cwh_' + bidx">
                  I currently work here
                </label>
              </div>
            </div>
            <div v-if="is_editing && work_experience_blocks.length < (_all_we_blocks || []).length && work_experience_blocks.length < 3" class="text-center mt-2 mb-3">
              <button class="btn btn-sm text-xxs shadow btn-primary-dark" @click="add_we_block">Add Experience</button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <FilePreviewComponent
    v-if="file_preview_modal"
    :file="{ presigned_url: file_preview_url, url: file_preview_url, file_name: file_preview_name, name: file_preview_name }"
    :file_name="file_preview_name"
    :presigned_url="file_preview_url"
    :key="file_preview_url || file_preview_name"
    @close="close_file_preview"
  />
</div>
</template>
<script src="./profile_info.js"></script>
<style scoped>
.profile-onboarding-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.profile-loading-state,
.profile-empty-state {
	width: 100%;
}

.profile-forms-container {
	padding: 0;
}

/* ── Accordion toolbar ── */
.profile-acc-toolbar {
	padding: 12px 16px 10px;
}

/* ── Accordion list ── */
.profile-acc-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 0 0 1rem;
}

/* ── Accordion item ── */
.profile-acc-item {
	background: rgba(255, 255, 255, 0.78);
	border: 0.5px solid var(--secondary-color, #e5e7eb);
	border-radius: 0.5rem;
	overflow: hidden;
}

/* ── Accordion head ── */
.profile-acc-head {
	width: 100%;
	background: none;
	border: none;
	padding: 12px 16px;
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	text-align: left;
}

.profile-acc-head:hover {
	background: rgba(0, 0, 0, 0.02);
}

.profile-acc-num {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: var(--secondary-color, #f3f4f6);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 500;
	color: var(--primary-color, #6b7280);
	flex-shrink: 0;
}

.profile-acc-title-wrap {
	flex: 1;
	min-width: 0;
}

.profile-acc-title {
	font-size: 13px;
	font-weight: 500;
	color: var(--primary-color, #1a2e5a);
	display: block;
}

.profile-acc-subtitle {
	font-size: 11px;
	color: #6b7280;
	margin-top: 1px;
	display: block;
}

/* ── Accordion body ── */
.profile-acc-body {
	border-top: 0.5px solid var(--secondary-color, #e5e7eb);
	padding: 12px 16px 16px;
}

.profile-acc-body-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.65rem;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid var(--secondary-color, #e5e7eb);
}

/* ── Form element styles (kept) ── */
.profile-subsection-card {
	padding: 1rem 1.1rem;
	border-radius: 18px;
	background: rgba(255, 255, 255, 0.92);
	border: 1px solid rgba(196, 177, 149, 0.35);
}

.profile-choice-group {
	padding: 0.25rem 0;
}

.profile-file-row {
	min-height: 2.5rem;
}

.profile-we-block-card {
	background: rgba(255, 255, 255, 0.92) !important;
	border-color: rgba(196, 177, 149, 0.35) !important;
}

.file-preview-icon-btn {
	line-height: 1;
	background: transparent;
	border: 0;
}

@media (max-width: 768px) {
	.profile-acc-body {
		padding: 10px 12px 14px;
	}

	.form-control,
	.form-select {
		min-height: 2.45rem;
		border-radius: 0.72rem !important;
		box-shadow: none !important;
		background: #f3f4f6 !important;
		border: 1px solid #e5e7eb !important;
	}

	textarea.form-control {
		min-height: 4.4rem;
	}

	.profile-subsection-card,
	.profile-we-block-card {
		padding: 1rem !important;
		border-radius: 1rem !important;
		border-color: #e5e7eb !important;
		background: #fff !important;
	}
}
</style>

